import { MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
	CALIBRATION_FILTERS,
	type ExternalProp,
	LOCATION_MODE_FILTERS,
	type RoutingEnv,
	type RoutingLocationFilters,
	type RoutingLocationPolicy,
} from "@/types/admin-portal/routing-mode";
import {
	type ActiveFilter,
	FilterPanel,
	FilterToggle,
	useFilterPanel,
} from "./filter-panel";
import { LoadError } from "./load-error";
import {
	BULK_MAX,
	BulkApplyDialog,
	EditLocationDialog,
	ResetLocationDialog,
} from "./location-dialogs";
import { matches, pageOf, togglePage } from "./locations";
import { SearchCombobox, toOptions } from "./search-combobox";

const ALL = "ALL";
const DEFAULT_LIMIT = 25;
const NO_FILTERS: RoutingLocationFilters = {
	state: "",
	city: "",
	mode: "",
	calibration: "",
};

interface LocationsTableProps {
	locations?: ExternalProp<RoutingLocationPolicy[]>;
	/** the env the backend serves; location policies apply to it alone */
	currentEnv?: RoutingEnv;
	/** route template containing ":id" */
	locationUrl: string;
	bulkUrl: string;
	onShowHistory: (locationId: number) => void;
}

export function LocationsTable({
	locations,
	currentEnv,
	locationUrl,
	bulkUrl,
	onShowHistory,
}: LocationsTableProps) {
	const { t } = useTranslation("routing-mode");
	const [filters, setFilters] = useState<RoutingLocationFilters>(NO_FILTERS);
	const panel = useFilterPanel("routing-mode-locations-preferences");
	const [dialog, setDialog] = useState<{
		kind: "edit" | "reset";
		location: RoutingLocationPolicy;
	} | null>(null);
	const [selected, setSelected] = useState<number[]>([]);
	const [bulkOpen, setBulkOpen] = useState(false);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(DEFAULT_LIMIT);
	const urlFor = (row: RoutingLocationPolicy) =>
		locationUrl.replace(":id", String(row.locationId));

	const all = locations?.data ?? [];
	const states = useMemo(() => toOptions(all.map((r) => r.state)), [all]);
	// Cities narrow to the chosen province.
	const cities = useMemo(
		() =>
			toOptions(
				all
					.filter((r) => !filters.state || r.state === filters.state)
					.map((r) => r.city),
			),
		[all, filters.state],
	);
	// The busiest location sets the scale, so each bar reads as its share of that.
	const maxVisits = useMemo(
		() => Math.max(0, ...all.map((r) => r.visits90d)),
		[all],
	);
	const rows = useMemo(
		() => all.filter((r) => matches(r, filters)),
		[all, filters],
	);

	function setFilter(patch: Partial<RoutingLocationFilters>) {
		// Rows that leave the list must not stay selected out of sight.
		setSelected([]);
		setPage(1);
		setFilters((prev) => {
			const next = { ...prev, ...patch };
			// A city from another province would hide every row.
			if (patch.state !== undefined && next.city) {
				const stillThere = all.some(
					(r) =>
						r.city === next.city && (!next.state || r.state === next.state),
				);
				if (!stillThere) next.city = "";
			}
			return next;
		});
	}

	if (locations?.error || !locations?.data) {
		return (
			<LoadError
				error={locations?.error ?? t("failed.body")}
				only={["locations"]}
			/>
		);
	}

	const {
		pages,
		current: currentPage,
		rows: pageRows,
	} = pageOf(rows, page, limit);
	// A Set: up to 500 picks are checked against every row on each render.
	const selectedSet = new Set(selected);
	const selectedRows = rows.filter((r) => selectedSet.has(r.locationId));
	const activeFilters: ActiveFilter[] = [
		filters.state && {
			key: "state",
			label: t("locations.filter.state"),
			value: filters.state,
			onClear: () => setFilter({ state: "" }),
		},
		filters.city && {
			key: "city",
			label: t("locations.filter.city"),
			value: filters.city,
			onClear: () => setFilter({ city: "" }),
		},
		filters.mode && {
			key: "mode",
			label: t("locations.columns.mode"),
			value: t(`locations.filter.${filters.mode}`),
			onClear: () => setFilter({ mode: "" }),
		},
		filters.calibration && {
			key: "calibration",
			label: t("locations.filter.calibration"),
			value: t(`locations.filter.${filters.calibration}`),
			onClear: () => setFilter({ calibration: "" }),
		},
	].filter((f): f is ActiveFilter => Boolean(f));

	// Select all covers the rows on screen; picks on other pages stay picked.
	const pageIds = pageRows.map((r) => r.locationId);
	const pageSelected = pageIds.filter((id) => selectedSet.has(id)).length;

	function toggle(id: number) {
		setSelected((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between gap-2">
				<FilterToggle
					open={panel.open}
					activeCount={activeFilters.length}
					onToggle={panel.toggle}
				/>
				<span className="text-sm text-muted-foreground">
					{t("locations.count", { shown: rows.length, total: all.length })}
				</span>
			</div>

			<FilterPanel
				open={panel.open}
				onClose={panel.toggle}
				onClearAll={() => setFilter(NO_FILTERS)}
				activeFilters={activeFilters}
			>
				<SearchCombobox
					options={states}
					value={filters.state}
					onChange={(state) => setFilter({ state })}
					allLabel={t("locations.filter.all_states")}
					searchPlaceholder={t("locations.filter.search_state")}
					emptyText={t("locations.filter.no_match")}
					ariaLabel={t("locations.filter.state")}
				/>
				<SearchCombobox
					options={cities}
					value={filters.city}
					onChange={(city) => setFilter({ city })}
					allLabel={t("locations.filter.all_cities")}
					searchPlaceholder={t("locations.filter.search_city")}
					emptyText={t("locations.filter.no_match")}
					ariaLabel={t("locations.filter.city")}
				/>
				<Select
					value={filters.mode || ALL}
					onValueChange={(v) =>
						setFilter({
							mode: v === ALL ? "" : (v as RoutingLocationFilters["mode"]),
						})
					}
				>
					<SelectTrigger
						className="bg-input shadow-inner"
						aria-label={t("locations.columns.mode")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>{t("locations.filter.all")}</SelectItem>
						{LOCATION_MODE_FILTERS.map((m) => (
							<SelectItem key={m} value={m}>
								{t(`locations.filter.${m}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select
					value={filters.calibration || ALL}
					onValueChange={(v) =>
						setFilter({
							calibration:
								v === ALL ? "" : (v as RoutingLocationFilters["calibration"]),
						})
					}
				>
					<SelectTrigger
						className="bg-input shadow-inner"
						aria-label={t("locations.filter.calibration")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>
							{t("locations.filter.all_calibration")}
						</SelectItem>
						{CALIBRATION_FILTERS.map((c) => (
							<SelectItem key={c} value={c}>
								{t(`locations.filter.${c}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</FilterPanel>

			{selectedRows.length > 0 && (
				<div className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-3 sm:flex-row sm:items-center sm:justify-between">
					<span className="text-sm font-medium">
						{selectedRows.length > BULK_MAX
							? t("locations.bulk.too_many", { max: BULK_MAX })
							: t("locations.bulk.selected", { count: selectedRows.length })}
					</span>
					<div className="flex gap-2">
						<Button variant="ghost" onClick={() => setSelected([])}>
							{t("locations.bulk.clear")}
						</Button>
						<Button
							onClick={() => setBulkOpen(true)}
							disabled={selectedRows.length > BULK_MAX}
						>
							{t("locations.bulk.open", { count: selectedRows.length })}
						</Button>
					</div>
				</div>
			)}

			{rows.length === 0 ? (
				<div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
					{t("locations.empty")}
				</div>
			) : (
				<div className="rounded-lg border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-10">
									<Checkbox
										checked={
											pageSelected === pageRows.length
												? true
												: pageSelected > 0
													? "indeterminate"
													: false
										}
										onCheckedChange={() =>
											setSelected((prev) => togglePage(prev, pageIds))
										}
										aria-label={t("locations.bulk.select_all")}
									/>
								</TableHead>
								<TableHead>{t("locations.columns.city")}</TableHead>
								<TableHead>{t("locations.columns.mode")}</TableHead>
								<TableHead>{t("locations.columns.effective")}</TableHead>
								<TableHead className="text-right">
									{t("locations.columns.visits")}
								</TableHead>
								<TableHead>{t("locations.edit.notes")}</TableHead>
								<TableHead>{t("locations.columns.tags")}</TableHead>
								<TableHead className="w-10" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{pageRows.map((row) => (
								<TableRow key={row.locationId}>
									<TableCell>
										<Checkbox
											checked={selectedSet.has(row.locationId)}
											onCheckedChange={() => toggle(row.locationId)}
											aria-label={t("locations.bulk.select_row", {
												city: row.city,
											})}
										/>
									</TableCell>
									<TableCell>
										<div className="font-medium">{row.city}</div>
										<div className="text-xs text-muted-foreground">
											{row.state}
										</div>
									</TableCell>
									<TableCell>
										<StoredMode row={row} />
									</TableCell>
									<TableCell>
										<EffectiveMode row={row} />
									</TableCell>
									<TableCell>
										<Visits count={row.visits90d} max={maxVisits} />
									</TableCell>
									<TableCell className="max-w-56">
										{row.notes ? (
											// clamped to two lines; the full note shows on hover
											<p
												title={row.notes}
												className="line-clamp-2 whitespace-pre-line text-xs"
											>
												{row.notes}
											</p>
										) : (
											<span className="text-xs text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell>
										<Tags row={row} />
									</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													aria-label={t("locations.actions_for", {
														city: row.city,
													})}
												>
													<MoreHorizontal className="size-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onSelect={() =>
														setDialog({ kind: "edit", location: row })
													}
												>
													{t("global.edit")}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={!row.configured}
													onSelect={() =>
														setDialog({ kind: "reset", location: row })
													}
												>
													{t("locations.reset.action")}
												</DropdownMenuItem>
												<DropdownMenuItem
													onSelect={() => onShowHistory(row.locationId)}
												>
													{t("locations.history_action")}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{rows.length > 0 && (
				<ApptPagination
					metadata={{
						page: currentPage,
						pages,
						limit,
						count: rows.length,
						from: (currentPage - 1) * limit + 1,
						to: (currentPage - 1) * limit + pageRows.length,
					}}
					label={t("locations.pagination_label")}
					actions={{
						goToPrevpage: () => setPage(currentPage - 1),
						goToNextPage: () => setPage(currentPage + 1),
						goToPage: (value) => setPage(Number(value)),
						goToFirstPage: () => setPage(1),
						goToLastPage: () => setPage(pages),
						onChangeLimit: (value) => {
							setLimit(Number(value));
							setPage(1);
						},
					}}
				/>
			)}

			{bulkOpen && (
				<BulkApplyDialog
					locations={selectedRows}
					env={currentEnv}
					url={bulkUrl}
					onClose={() => setBulkOpen(false)}
					onDone={() => {
						setBulkOpen(false);
						setSelected([]);
					}}
				/>
			)}
			{dialog?.kind === "edit" && (
				<EditLocationDialog
					location={dialog.location}
					env={currentEnv}
					url={urlFor(dialog.location)}
					onClose={() => setDialog(null)}
				/>
			)}
			{dialog?.kind === "reset" && (
				<ResetLocationDialog
					location={dialog.location}
					url={urlFor(dialog.location)}
					onClose={() => setDialog(null)}
				/>
			)}
		</div>
	);
}

/** What this location is set to; an unconfigured one reads as the default. */
function StoredMode({ row }: { row: RoutingLocationPolicy }) {
	const { t } = useTranslation("routing-mode");

	if (!row.configured) {
		return (
			<span className="inline-flex items-center rounded-md border border-dashed px-2 py-0.5 text-xs text-muted-foreground">
				{t("locations.stored.default")}
			</span>
		);
	}
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
				row.mode === "HAVERSINE"
					? "border-primary/30 bg-primary/10 text-primary"
					: "bg-secondary text-secondary-foreground",
			)}
		>
			{t(`locations.mode_label.${row.mode}`)}
		</span>
	);
}

/**
 * What a search uses right now. It only needs a reason when something other than the
 * location's own setting decides it — above all when that overrides the stored mode.
 */
function EffectiveMode({ row }: { row: RoutingLocationPolicy }) {
	const { t } = useTranslation("routing-mode");
	const decidedHere = row.effectiveSource === "location";
	const overridden =
		(row.configured && row.mode !== row.effectiveMode) ||
		row.effectiveSource === "env_disabled";

	return (
		<div className="flex flex-col gap-0.5">
			<span className="flex items-center gap-2 text-sm">
				<span
					aria-hidden
					className={cn(
						"size-2 shrink-0 rounded-full",
						overridden
							? "bg-amber-500"
							: decidedHere
								? "bg-emerald-500"
								: "bg-muted-foreground/50",
					)}
				/>
				{t(`locations.mode_label.${row.effectiveMode}`)}
			</span>
			{!decidedHere && (
				<span
					className={cn(
						"pl-4 text-xs",
						overridden
							? "text-amber-700 dark:text-amber-400"
							: "text-muted-foreground",
					)}
				>
					{overridden
						? t(`locations.overridden.${row.effectiveSource}`)
						: t(`locations.source.${row.effectiveSource}`)}
				</span>
			)}
		</div>
	);
}

/** The visit count with a bar scaled to the busiest location. */
function Visits({ count, max }: { count: number; max: number }) {
	const { t } = useTranslation("routing-mode");

	const share = (count / max) * 100;
	const hint = t("locations.visits.bar_hint", {
		share: Math.round(share),
		max: max.toLocaleString("id-ID"),
	});

	if (count === 0) {
		return (
			<span className="block text-right text-xs text-muted-foreground">
				{t("locations.visits.none")}
			</span>
		);
	}
	return (
		<div className="ml-auto flex w-28 flex-col items-end">
			<span className="text-sm">
				<span className="font-semibold tabular-nums">
					{count.toLocaleString("id-ID")}
				</span>{" "}
				<span className="text-xs text-muted-foreground">
					{t("locations.visits.unit", { count })}
				</span>
			</span>
			<TooltipProvider delayDuration={150}>
				<Tooltip>
					<TooltipTrigger asChild>
						{/* taller than the bar so it is easy to hover */}
						<button
							type="button"
							aria-label={hint}
							className="w-full cursor-help rounded-sm py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							<div className="h-1 w-full rounded-full bg-muted">
								<div
									className="h-full rounded-full bg-primary/70"
									style={{ width: `${Math.max(4, share)}%` }}
								/>
							</div>
						</button>
					</TooltipTrigger>
					<TooltipContent side="left" className="max-w-64 text-xs">
						{hint}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
}

/** Calibration at a glance: the road factor and speed, or a flag when there is none. */
function Tags({ row }: { row: RoutingLocationPolicy }) {
	const { t } = useTranslation("routing-mode");

	if (!row.calibrated) {
		return (
			<Badge
				variant="outline"
				className="border-amber-500 text-amber-700 dark:text-amber-400"
			>
				{t("locations.uncalibrated")}
			</Badge>
		);
	}
	return (
		<div className="flex flex-wrap gap-1">
			<Badge variant="outline" className="tabular-nums">
				{t("locations.tags.factor", {
					value: row.circuityFactor?.toLocaleString("id-ID", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}),
				})}
			</Badge>
			{row.speedKmh != null && (
				<Badge variant="outline" className="tabular-nums">
					{t("locations.tags.speed", {
						value: row.speedKmh.toLocaleString("id-ID"),
					})}
				</Badge>
			)}
		</div>
	);
}
